import { Injectable, inject } from '@angular/core';
import { ColumnPreferencesService, TableColumnPrefs } from './column-preferences.service';

@Injectable({ providedIn: 'root' })
export class ColumnPreferencesHelperService {
  private columnPrefService = inject(ColumnPreferencesService);

  /**
   * Initialize columns and visibility from canonical definition and persisted preferences.
   * Returns the table columns object (visible columns only) plus helper metadata.
   */
  initialize(
    tableId: string,
    canonical: Record<string, any>,
    options?: { defaultHidden?: string[]; defaultVisibility?: Record<string, boolean>; minVersion?: number }
  ) {
    let prefs = this.columnPrefService.getPreferences(tableId);
    if (options?.minVersion && (prefs === null || (prefs.version as number) < options.minVersion)) {
      prefs = null;
    }

    const canonArr = Object.keys(canonical).map((k, idx) => ({ key: k, col: canonical[k], defaultIndex: idx }));

    let finalArr: { key: string; col: any; order: number }[];

    if (prefs && Array.isArray(prefs.columns) && prefs.columns.length > 0) {
      finalArr = prefs.columns
        .map((pref) => {
          const found = canonArr.find((c) => c.key === pref.key);
          if (!found) return null;
          const cloned = { ...found.col } as any;
          return { key: found.key, col: cloned, order: pref.order ?? found.defaultIndex };
        })
        .filter(Boolean) as any[];

      // add missing columns that were added in code after prefs saved
      const missing = canonArr.filter((c) => !finalArr.find((f) => f.key === c.key));
      missing.forEach((m) => finalArr.push({ key: m.key, col: m.col, order: m.defaultIndex }));
      finalArr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    } else {
      finalArr = canonArr.map((c) => ({ key: c.key, col: c.col, order: c.defaultIndex }));
    }

    // persistent column order (includes invisible columns)
    let columnOrder: string[];
    if (prefs && prefs.columns && prefs.columns.length > 0) {
      // drop saved keys no longer defined in code (renamed/removed columns)
      columnOrder = prefs.columns.map((c) => c.key).filter((k) => k in canonical);
      // ensure any new canonical keys are appended at the end in canonical order
      Object.keys(canonical).forEach((k) => {
        if (!columnOrder.includes(k)) columnOrder.push(k);
      });
    } else {
      columnOrder = Object.keys(canonical);
    }

    // build tableConfig.columns object and visibility map
    const cols: Record<string, any> = {};
    const columnKeys = Object.keys(canonical);
    const columnVisibility: Record<string, boolean> = {};

    // set visibility according to prefs (default visible) and build cols following columnOrder
    const defaultVisibility = options?.defaultVisibility
      ? options.defaultVisibility
      : options?.defaultHidden
      ? Object.fromEntries(options.defaultHidden.map((k) => [k, false]))
      : {};

    columnOrder.forEach((k) => {
      const prefEntry = prefs && prefs.columns ? prefs.columns.find((c) => c.key === k) : null;
      const defaultVisible = canonical[k]?.display !== false;
      const isVisible = prefEntry
        ? prefEntry.visible !== false
        : defaultVisibility[k] !== undefined
        ? defaultVisibility[k]
        : defaultVisible;
      columnVisibility[k] = isVisible;
      if (isVisible) {
        cols[k] = canonical[k];
      }
    });

    const columnNames: Record<string, string> = {};
    Object.keys(canonical).forEach((k) => (columnNames[k] = canonical[k].header));

    return { tableColumns: cols, columnNames, columnKeys, columnOrder, columnVisibility };
  }

  toggleAndSave(
    tableId: string,
    key: string,
    canonical: Record<string, any>,
    columnOrder: string[],
    columnVisibility: Record<string, boolean>
  ) {
    columnVisibility[key] = !columnVisibility[key];

    const cols: Record<string, any> = {};
    columnOrder.forEach((k) => {
      if (columnVisibility[k]) {
        cols[k] = canonical[k];
      }
    });

    // persist
    this.save(tableId, columnOrder, columnVisibility, canonical);

    return { tableColumns: cols, columnVisibility };
  }

  save(tableId: string, columnOrder: string[], columnVisibility: Record<string, boolean>, canonical: Record<string, any>): void {
    const cols = columnOrder.map((k, idx) => ({ key: k, visible: columnVisibility[k] !== false, order: idx }));
    // ensure any completely new canonical keys are included
    Object.keys(canonical).forEach((k) => {
      if (!cols.find((c) => c.key === k)) {
        cols.push({ key: k, visible: columnVisibility[k] !== false, order: cols.length });
      }
    });

    const prefs: TableColumnPrefs = { tableId, version: 1, columns: cols };
    this.columnPrefService.savePreferences(tableId, prefs);
  }
}
